import { FormControl, Grid, InputLabel, MenuItem, TextField, Typography } from '@mui/material';
import { useMemo } from 'react';
import { Controller, FormProvider, UseFormReturn } from 'react-hook-form';
import { useUser } from '../../Hooks/useUser';
import { setHasMembership } from '../../Redux/slices/JobPostingCreationSlice';
import { useAppDispatch } from '../../Redux/store';
import { languages } from '../../Types/General';
import { DurationType, JobType, PaymentType, WorkSite } from '../../Types/Job';
import { KaziSpecializations } from '../Kazi/KaziSpecializations';
import PaymentPreferences from './PaymentPreferences';

interface TalentPreferenceProps {    
    form: UseFormReturn<any>
}

export const TalentPreference = ({ form }: TalentPreferenceProps) => {
    /* ******************** Hooks ******************** */
    const dispatch = useAppDispatch();
    const { hasPremium } = useUser();
    const { control, watch } = form;
    /* ******************** Variables ******************** */
    const jobType = watch('type');
    const payTypes = useMemo(() => {
        let paymentTypes = PaymentType;
        if (jobType === 'Short-term') paymentTypes = PaymentType.filter( t => t == 'One-time');
        if (jobType === 'Full-time' || jobType === 'Internship') {
            dispatch(setHasMembership(hasPremium))
            paymentTypes = PaymentType.filter( t => t !== 'One-time')
        } else {
            dispatch(setHasMembership(true));
        }
        return paymentTypes
    }, [jobType])
    /* ******************** Functions ******************** */
    /* ******************** Effects ******************** */
    /* ******************** JSX ******************** */
    return (
        <FormProvider {...form}>
            <Grid container display={"flex"} flexDirection={"column"} gap={'20px'}>
                <Typography variant='lora-title-large' color={'primary'} fontWeight={'bold'}>Who are you looking to hire?</Typography> 
                <Grid item display={"flex"} flexDirection={"row"} gap={'12px'}>
                    <Grid  item display={"flex"} flexDirection={"column"} flex={1}>
                        <Typography variant='body-small' color={'black.200'} marginBottom={1}>Job category</Typography> 
                        <Controller
                            name='title'
                            control={control}
                            render={({field}) =>(    
                                <KaziSpecializations 
                                    {...field}
                                    // excludeList={getExcludeList(field.name)}
                                    placeholder='Job category'
                                    fullWidth
                                    // textFieldProps={
                                    //     {
                                    //         sx: {
                                    //             minWidth: "270px"
                                    //         }
                                    //     }
                                    // }
                                />
                            )}
                        />
                    </Grid>                            
                    <Grid  item display={"flex"} flexDirection={"column"} flex={1}>
                        <Typography variant='body-small' color={'black.200'} marginBottom={1}>Type of position</Typography> 
                        <Controller
                            name='type'
                            control={control}
                            render={({field}) =>(                                           
                                <FormControl>   
                                    <InputLabel>{field.value ?"":"Type of position"}</InputLabel>
                                    <TextField
                                        {...field}
                                        select
                                    >
                                        {
                                            JobType.map((type) => (
                                                <MenuItem key={type} value={type}>
                                                    {type}
                                                </MenuItem>
                                            ))
                                        }
                                    </TextField>
                                </FormControl>
                            )}
                        />
                    </Grid>
                </Grid> 
                <Grid item display={"flex"} flexDirection={"row"} gap={'12px'}>
                    <Grid  item display={"flex"} flexDirection={"column"} flex={1}>
                        <Typography variant='body-small' color={'black.200'} marginBottom={1}>Work-site</Typography> 
                        <Controller
                            name='work_site'
                            control={control}
                            render={({field}) =>(                                         
                                <FormControl>   
                                    <InputLabel>{field.value ?"":"Work-site"}</InputLabel>
                                    <TextField
                                        {...field}
                                        select
                                    >
                                        {
                                            WorkSite.map((site) => (
                                                <MenuItem key={site} value={site}>
                                                    {site}
                                                </MenuItem>
                                            ))
                                        }
                                    </TextField>
                                </FormControl>
                            )}
                        />
                    </Grid>                            
                    <Grid  item display={"flex"} flexDirection={"column"} flex={1}>
                        <Typography variant='body-small' color={'black.200'} marginBottom={1}>Primary language</Typography> 
                        <Controller
                            name='primary_language'
                            control={control}
                            render={({field}) =>(                                         
                                <FormControl>   
                                    <InputLabel>{field.value ?"":"Language"}</InputLabel>
                                    <TextField
                                        {...field}
                                        select
                                    >
                                        {
                                            languages.map((language) => (
                                                <MenuItem key={language} value={language}>
                                                    {language}
                                                </MenuItem>
                                            ))
                                        }
                                    </TextField>
                                </FormControl>
                            )}
                        />
                    </Grid>
                </Grid>
                <Grid item display={"flex"} flexDirection={"row"} gap={'12px'}>
                    <PaymentPreferences 
                        {
                            ...{
                                jobType
                            }
                        }
                    />
                </Grid>
                {
                    jobType === 'Internship' &&
                    <Grid item display={"flex"} flexDirection={"row"} gap={'12px'}>
                        <Grid  item display={"flex"} flexDirection={"column"} flex={1}>
                            <Typography variant='body-small' color={'black.200'} marginBottom={1}>Length of internship</Typography> 
                            <Controller
                                name='duration'
                                control={control}
                                render={({field}) =>(
                                    <TextField 
                                        {...field}
                                        type='number'
                                        placeholder='Length of internship'
                                    />
                                )}
                            />
                        </Grid>                            
                        <Grid  item display={"flex"} flexDirection={"column"} flex={1}>
                            <Typography variant='body-small' color={'black.200'} marginBottom={1}>Length type</Typography> 
                            <Controller
                                name='duration_type'
                                control={control}
                                render={({field}) =>(                                         
                                    <FormControl>   
                                        <InputLabel>{field.value ?"":"Type"}</InputLabel>
                                        <TextField
                                            {...field}
                                            select
                                        >
                                            {
                                                DurationType.map((length) => (
                                                    <MenuItem key={length} value={length}>
                                                        {length}
                                                    </MenuItem>
                                                ))
                                            }
                                        </TextField>
                                    </FormControl>
                                )}
                            />
                        </Grid>
                    </Grid>  
                }
                <Grid item display={"flex"} flexDirection={"row"} gap={'12px'}>
                    {
                        jobType == 'Short-term' &&
                        <Typography color={'black.200'}  variant={'body-small'}>
                            *When you post a job, the payment amount will be taken out of your account automatically and held in escrow until the job has been marked complete.
                        </Typography>
                    }
                </Grid>
            </Grid>
        </FormProvider>
    )
}
